import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Container, Spinner, Card } from 'react-bootstrap';
import Layout from './Layout';
import { useAuth } from './../Context/AuthContext';
import { BASE_URL } from '../constants';

const NotificationDetails = () => {
  const { notificationId } = useParams(); // ID from route params
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const fetchUserNotifications = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${BASE_URL}/shelters/change-request-by-notification/${notificationId}`);
        setRequest(response.data.changeRequest || null);
      } catch (err) {
        setError('Failed to fetch notifications.');
      } finally {
        setLoading(false);
      }
    };

    if (notificationId) {
      fetchUserNotifications();
    }
  }, [notificationId]);

  const handleAccept = async (id) => {
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/shelters/respond/${id}`, {
        status: 'approved',
        responseMessage: 'Request approved and room assigned',
      });

      setRequest((prev) => ({
        ...prev,
        status: 'approved',
        responseMessage: 'Request approved and room assigned',
      }));

      setActionMessage('Request accepted successfully!');
      setTimeout(() => {
        setActionMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error accepting request:', err);
      setActionMessage('Failed to accept request.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/shelters/respond/${id}`, {
        status: 'rejected',
        responseMessage: 'Request rejected',
      });

      setRequest((prev) => ({
        ...prev,
        status: 'rejected',
        responseMessage: 'Request rejected',
      }));

      setActionMessage('Request rejected successfully!');
      setTimeout(() => {
        setActionMessage('');
      }, 3000);
    } catch (err) {
      setActionMessage('Failed to reject request.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Layout>
      <Container className="py-4 px-0">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-7">
            <Card className="shadow-lg border-0 rounded">
              <Card.Body className="p-6">
                <h4 className="mb-4 text-center text-primary">
                  {request?.requestType === 'Change Shelter'
                    ? 'Shelter Change Request'
                    : 'Room Change Request'}
                </h4>

                {error && (
                  <div className="alert alert-danger text-center">{error}</div>
                )}

                {request && Object.keys(request).length > 0 ? (
                  <div className="list-group-item">
                    <h5>
                      {request.userId.firstName} {request.userId.lastName}
                    </h5>
                    <p>
                      <strong>Reason:</strong> {request.reason}
                    </p>

                    {/* Shelter change info */}
                    {request.requestType === 'Change Shelter' && user?.isState && (
                      <div className="d-flex align-items-center mb-3">
                        <p className="mb-0 me-2">
                          <strong>From Shelter:</strong> {request.currentShelterId.name}
                        </p>
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="text-secondary mx-3"
                        />
                        <p className="mb-0">
                          <strong>To Shelter:</strong> {request.requestedShelterId.name}
                        </p>
                      </div>
                    )}

                    {/* Room change info */}
                    {request.currentRoomId && request.roomId && (
                      <div className="d-flex align-items-center mb-3">
                        <p className="mb-0 me-2">
                          <strong>From Room:</strong> {request.currentRoomId.roomNumber}
                        </p>
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="text-secondary mx-3"
                        />
                        <p className="mb-0">
                          <strong>To Room:</strong> {request.roomId.roomNumber}
                          {request.roomId.capacity && (
                            <> (Capacity: {request.roomId.capacity})</>
                          )}
                        </p>
                      </div>
                    )}

                    {/* Action messages */}
                    {actionMessage && (
                      <div
                        className={`alert ${
                          actionMessage.includes('successfully')
                            ? 'alert-success'
                            : 'alert-danger'
                        } mt-3`}
                      >
                        {actionMessage}
                      </div>
                    )}

                    {/* Action buttons */}
                    {!actionMessage &&
                      request.status !== 'approved' &&
                      request.status !== 'rejected' && (
                        <div className="d-flex justify-content-end mt-4">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-success me-3"
                            style={{ cursor: 'pointer', fontSize: '2rem' }}
                            onClick={() => handleAccept(request._id)}
                            disabled={actionLoading}
                          />
                          <FontAwesomeIcon
                            icon={faTimes}
                            className="text-danger"
                            style={{ cursor: 'pointer', fontSize: '2rem' }}
                            onClick={() => handleReject(request._id)}
                            disabled={actionLoading}
                          />
                        </div>
                      )}
                  </div>
                ) : (
                  !loading && <p className="text-center">No change request found.</p>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default NotificationDetails;
